import React from "react";
import { Helmet } from "react-helmet-async";

const DEFAULT_TITLE =
    "SRT Finder | Buy, Sell & Trade Dodge Hellcat, Challenger SRT, Charger SRT, TRX, Demon & Scat Pack | Mopar Performance Marketplace";
const DEFAULT_DESCRIPTION =
    "SRT Finder is the #1 dedicated marketplace and community for high-performance Mopar vehicles. Buy, sell, and trade Dodge Charger SRT Hellcat, Challenger SRT Hellcat Redeye, Widebody Scat Pack, SRT Demon & Demon 170, Chrysler 300 SRT8, and RAM TRX & RHO trucks. Verified sellers, real market pricing, expert community. SRT and R/T trim only — no V6s.";
const SITE_URL = "https://srtfinder.com";
const DEFAULT_IMAGE = `${SITE_URL}/srt-finder-logo.png`;

/**
 * Universal SEO + GEO (Generative Engine Optimization) component.
 * - Sets title, meta description, canonical, OG, Twitter, robots, keywords.
 * - Emits optional JSON-LD structured data blobs so AI answer engines
 *   (ChatGPT, Perplexity, Google AI Overviews, Claude, Gemini) can extract
 *   accurate facts about listings, posts, sellers and pages.
 */
export default function Seo({
    title,
    description,
    canonicalPath,
    image,
    imageAlt,
    keywords,
    type = "website",
    noindex = false,
    jsonLd,
    breadcrumbs, // [{name, path}] — will emit BreadcrumbList schema
    children,
}) {
    const fullTitle = title
        ? `${title} | SRT Finder`
        : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const canonical = canonicalPath
        ? `${SITE_URL}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`
        : SITE_URL + "/";
    const img = image || DEFAULT_IMAGE;
    const kw =
        keywords ||
        "SRT Hellcat for sale, Dodge Hellcat marketplace, Dodge Charger SRT Hellcat, Dodge Challenger Hellcat Redeye, buy Hellcat Widebody, Scat Pack Widebody for sale, SRT Demon 170 for sale, RAM TRX for sale, RAM RHO, Chrysler 300 SRT8, Mopar performance marketplace, buy sell trade Mopar, supercharged HEMI, muscle car marketplace, SRT community, Hellcat community";

    const jsonLdBlobs = [];
    if (jsonLd) {
        if (Array.isArray(jsonLd)) jsonLdBlobs.push(...jsonLd);
        else jsonLdBlobs.push(jsonLd);
    }
    if (breadcrumbs && breadcrumbs.length) {
        jsonLdBlobs.push({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: b.name,
                item: `${SITE_URL}${b.path.startsWith("/") ? b.path : `/${b.path}`}`,
            })),
        });
    }

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={desc} />
            <meta name="keywords" content={kw} />
            <meta
                name="robots"
                content={
                    noindex
                        ? "noindex, nofollow"
                        : "index, follow, max-image-preview:large"
                }
            />
            <link rel="canonical" href={canonical} />

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="SRT Finder" />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:image" content={img} />
            {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
            <meta property="og:locale" content="en_US" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@srtfinder" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={img} />

            {jsonLdBlobs.map((blob, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(blob)}
                </script>
            ))}
            {children}
        </Helmet>
    );
}

// ---------- GEO / Schema helpers ----------

export function vehicleListingSchema(listing) {
    if (!listing) return null;
    const url = `${SITE_URL}/listings/${listing.listing_id}`;
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: `${listing.year} ${listing.make} ${listing.model_name} ${listing.trim}`,
        description:
            listing.description ||
            `${listing.year} ${listing.make} ${listing.model_name} ${listing.trim} for sale on SRT Finder — the exclusive Mopar performance marketplace.`,
        image: (listing.photos || []).map((p) =>
            p?.startsWith?.("http") ? p : `${SITE_URL}${p}`,
        ),
        brand: { "@type": "Brand", name: listing.make },
        category: "Vehicles > Cars, Trucks & Motorcycles > Cars",
        sku: listing.listing_id,
        vehicleModelDate: String(listing.year),
        offers: {
            "@type": "Offer",
            url,
            priceCurrency: "USD",
            price: listing.price,
            availability:
                listing.status === "active"
                    ? "https://schema.org/InStock"
                    : listing.status === "sold"
                      ? "https://schema.org/SoldOut"
                      : "https://schema.org/Discontinued",
            itemCondition: "https://schema.org/UsedCondition",
            seller: listing.seller
                ? {
                      "@type": "Person",
                      name: listing.seller.name,
                      url: `${SITE_URL}/users/${listing.seller.user_id}`,
                  }
                : undefined,
        },
        additionalProperty: [
            { "@type": "PropertyValue", name: "Mileage", value: `${listing.mileage} mi` },
            { "@type": "PropertyValue", name: "Engine", value: listing.engine },
            { "@type": "PropertyValue", name: "Drivetrain", value: listing.drivetrain },
            { "@type": "PropertyValue", name: "Transmission", value: listing.transmission },
            { "@type": "PropertyValue", name: "Trim", value: listing.trim },
            { "@type": "PropertyValue", name: "Location", value: listing.location },
            { "@type": "PropertyValue", name: "Modifications", value: listing.mods_level },
        ],
        aggregateRating:
            listing.seller?.seller_rating > 0
                ? {
                      "@type": "AggregateRating",
                      ratingValue: listing.seller.seller_rating,
                      reviewCount: listing.seller.seller_rating_count || 1,
                      bestRating: 5,
                  }
                : undefined,
    };
}

export function articleSchema(post) {
    if (!post) return null;
    return {
        "@context": "https://schema.org",
        "@type": "DiscussionForumPosting",
        headline: post.title,
        articleBody: post.body,
        datePublished: post.created_at,
        author: post.author
            ? {
                  "@type": "Person",
                  name: post.author.name,
                  url: `${SITE_URL}/users/${post.author.user_id}`,
              }
            : undefined,
        image: (post.photos || []).map((p) =>
            p?.startsWith?.("http") ? p : `${SITE_URL}${p}`,
        ),
        interactionStatistic: [
            {
                "@type": "InteractionCounter",
                interactionType: "https://schema.org/LikeAction",
                userInteractionCount: post.upvotes || 0,
            },
            {
                "@type": "InteractionCounter",
                interactionType: "https://schema.org/CommentAction",
                userInteractionCount: post.comment_count || 0,
            },
        ],
        url: `${SITE_URL}/community/${post.post_id}`,
    };
}

export function personSchema(user) {
    if (!user) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Person",
        name: user.name,
        description: user.bio,
        image: user.avatar_url,
        url: `${SITE_URL}/users/${user.user_id}`,
        address: user.location
            ? { "@type": "PostalAddress", addressLocality: user.location }
            : undefined,
        aggregateRating:
            user.seller_rating > 0
                ? {
                      "@type": "AggregateRating",
                      ratingValue: user.seller_rating,
                      reviewCount: user.seller_rating_count || 1,
                      bestRating: 5,
                  }
                : undefined,
    };
}

export function itemListSchema(listings) {
    if (!listings?.length) return null;
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: listings.slice(0, 20).map((l, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/listings/${l.listing_id}`,
            name: `${l.year} ${l.make} ${l.model_name} ${l.trim}`,
        })),
    };
}

export function faqSchema(faqs) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };
}


export function partSchema(part) {
    if (!part) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: part.title,
        description: part.description,
        category: part.category,
        image: (part.photos || []).map((p) => (p?.startsWith?.("http") ? p : `${SITE_URL}${p}`)),
        brand: { "@type": "Brand", name: "SRT / Mopar Performance Aftermarket" },
        offers: {
            "@type": "Offer",
            price: part.price,
            priceCurrency: "USD",
            itemCondition:
                part.condition === "New"
                    ? "https://schema.org/NewCondition"
                    : "https://schema.org/UsedCondition",
            availability:
                part.status === "sold"
                    ? "https://schema.org/SoldOut"
                    : "https://schema.org/InStock",
            seller: part.seller
                ? { "@type": "Person", name: part.seller.name, url: `${SITE_URL}/users/${part.seller.user_id}` }
                : undefined,
            url: `${SITE_URL}/parts/${part.part_id}`,
        },
    };
}

export function eventSchema(ev) {
    if (!ev) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Event",
        name: ev.title,
        description: ev.description,
        startDate: ev.event_date,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: { "@type": "Place", name: ev.location, address: ev.location },
        organizer: ev.owner
            ? { "@type": "Person", name: ev.owner.name, url: `${SITE_URL}/users/${ev.owner.user_id}` }
            : { "@type": "Organization", name: "SRT Finder Community" },
        image: ev.photos?.[0],
        url: `${SITE_URL}/events`,
    };
}

export function organizationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "SRT Finder",
        url: SITE_URL,
        logo: `${SITE_URL}/srt-finder-logo.png`,
        description:
            "The exclusive marketplace and community for high-performance Mopar vehicles — Dodge SRT Hellcat, Charger, Challenger, Chrysler 300 SRT, RAM TRX and RHO.",
    };
}

export function websiteSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "SRT Finder",
        url: SITE_URL,
        potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/marketplace?search={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    };
}
